import PropTypes from 'prop-types';
import React from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import PdfViewer from '../components/PdfViewer';
import { handleDownloadClick, DOWNLOAD_WARNING } from '../helpers/PdfViewerHelpers';
import { isAndroid } from '../helpers/BrowserHelpers';

class PdfViewerContainer extends React.Component {
  state = {
    pdfLoading: true,
    doc: null,
    page: 1,
    previousDisabled: true,
    nextDisabled: false,
    pageStatus: '',
  };

  componentDidUpdate(prevProps) {
    if ((!prevProps.open && this.props.open) || (this.props.open && prevProps.attachmentUrl !== this.props.attachmentUrl)) {
      this.setState({
        pdfLoading: true,
        doc: null,
        page: 1,
        previousDisabled: true,
        nextDisabled: false,
        pageStatus: '',
      });
      this.getPDF();
    }
  }

  getPDF() {
    const params = {
      withCredentials: true,
      url: this.props.attachmentUrl,
      // pdf.js makes its own XHR, which otherwise sends only the session cookie (no Bearer).
      // That cookie can lapse while the SPA stays open → 401. Pass the same Authorization
      // header the rest of the app uses (axios defaults, refreshed on token refresh) so the
      // route authenticates by Bearer like image/download requests do.
      httpHeaders: { Authorization: axios.defaults.headers.common.Authorization },
    };

    window.pdfjsLib.getDocument(params).promise.then((doc) => {
      this.setState({
        doc,
        pdfLoading: false,
        nextDisabled: (doc.numPages === 1),
      }, () => {
        this.renderPage();
      });
    });
  }

  handleNavClick = (direction) => {
    if (direction === 'previous') {
      if (this.state.page > 1) {
        this.renderPage((this.state.page - 1), direction);
      }
    } else {
      if (this.state.page + 1 <= this.state.doc.numPages) { // eslint-disable-line no-lonely-if
        this.renderPage((this.state.page + 1), direction);
      }
    }
  }

  // Download is a REAL <a> click (see PdfViewer). Native: let it navigate to the route URL —
  // the shell's download handler fetches it (following the redirect to S3) and saves it. A
  // programmatic link.click() never fires the shell, which is why the old download did nothing
  // on iOS. Web: the route redirects to an inline S3 URL (would open a new tab), so blob-save.
  handleDownload = async (e) => {
    if (this.props.isNativeApp) {
      if (!window.confirm(DOWNLOAD_WARNING)) e.preventDefault(); // eslint-disable-line no-alert
      return;
    }
    e.preventDefault();
    if (!window.confirm(DOWNLOAD_WARNING)) return; // eslint-disable-line no-alert
    try {
      const { data } = await axios.get(this.props.attachmentUrl, { responseType: 'blob' });
      handleDownloadClick(URL.createObjectURL(data), this.props.fileName, this.props.isNativeApp, true);
    } catch {
      window.open(this.props.attachmentUrl, '_blank', 'noopener');
    }
  }

  renderPage = (page = this.state.page) => {
    const scale = 1.5;
    const container = this.pdfContainer;
    const { doc } = this.state;

    if (container) {
      container.innerHTML = '';
    }

    doc.getPage(page).then((pdfPage) => {
      const outputScale = window.devicePixelRatio || 1;
      const viewport = pdfPage.getViewport({ scale });
      const pageWrapper = document.createElement('div');
      pageWrapper.className = 'page';

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.width = Math.floor(viewport.width * outputScale);

      pageWrapper.appendChild(canvas);
      container.appendChild(pageWrapper);

      const renderContext = {
        canvasContext: context,
        transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
        viewport,
      };

      this.setState({
        pageStatus: `Page ${page} of ${doc.numPages}`,
        page,
      }, () => {
        this.setState({
          previousDisabled: (this.state.page === 1),
          nextDisabled: (this.state.page >= this.state.doc.numPages),
        });
      });

      pdfPage.render(renderContext);
    });
  }

  render() {
    const props = {
      handleDownload: this.handleDownload,
      downloadHref: this.props.attachmentUrl,
      fileName: this.props.fileName,
      isNativeApp: this.props.isNativeApp,
      isNativeAndroid: this.props.isNativeApp && isAndroid(),
      handlePdfViewerClose: this.props.handlePdfViewerClose,
      coverTitle: this.props.fileName,
      pdfContainerRef: (pdfContainer) => (this.pdfContainer = pdfContainer),
      navPreviousRef: (navPrevious) => (this.navPrevious = navPrevious),
      navNextRef: (navNext) => (this.navNext = navNext),
      previousDisabled: this.state.previousDisabled,
      nextDisabled: this.state.nextDisabled,
      pageStatus: this.state.pageStatus,
      handleNavClick: this.handleNavClick,
      attachmentUrl: this.props.attachmentUrl,
      pdfLoading: this.state.pdfLoading,
      open: this.props.open,
      className: this.props.className,
    };

    return (
      <PdfViewer {...props} />
    );
  }
}

PdfViewerContainer.propTypes = {
  className: PropTypes.string,
  attachmentUrl: PropTypes.string,
  data: PropTypes.string,
  fileName: PropTypes.string,
  open: PropTypes.bool.isRequired,
  handlePdfViewerClose: PropTypes.func.isRequired,
  isNativeApp: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isNativeApp: state.nativeApp.isNativeApp,
});

export default connect(mapStateToProps)(PdfViewerContainer);
